import { useState } from "react";
import { Shield, HeartPulse, Boxes, LifeBuoy, ShieldCheck, Lock, Fingerprint, Info, ChevronRight, Loader2 } from "lucide-react";
import { useVault, COMPARTMENT } from "../hooks/useVault";
import VaultOnboarding from "../components/private-vault/VaultOnboarding";
import VaultUnlock from "../components/private-vault/VaultUnlock";
import HealthCompartment from "../components/private-vault/HealthCompartment";
import InventoryCompartment from "../components/private-vault/InventoryCompartment";
import EmergencyCardExport from "../components/private-vault/EmergencyCardExport";
import BackupPanel from "../components/private-vault/BackupPanel";
import BackupReminderBanner from "../components/private-vault/BackupReminderBanner";

/**
 * PrivateVault — the new local-first, on-device vault (iter216).
 * Replaces the cloud-backed Safe Vault at /vault. No network calls happen
 * here — everything is IndexedDB + Web Crypto in the user's browser.
 */
export default function PrivateVault() {
  const vault = useVault();
  const [section, setSection] = useState(null);      // health | inventory | emergency | backup

  // Loading — first check for meta
  if (vault.initialized === null) {
    return (
      <div className="min-h-screen bg-slate-50 grid place-items-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  // Not initialized — run onboarding
  if (!vault.initialized) {
    return (
      <div className="min-h-screen bg-slate-50 py-6">
        <VaultOnboarding vault={vault} onDone={() => { /* stays unlocked from bootstrap */ }} />
      </div>
    );
  }

  // Initialized but locked — prompt unlock
  if (!vault.unlocked) {
    return (
      <div className="min-h-screen bg-slate-50 grid place-items-center py-10">
        <VaultUnlock vault={vault} onUnlocked={() => setSection(null)} />
      </div>
    );
  }

  // Unlocked — render the picked section, or the home hub
  return (
    <div className="min-h-screen bg-slate-50 py-4" data-testid="private-vault">
      {section === "health"    && <HealthCompartment    vault={vault} compartmentId={COMPARTMENT.HEALTH}    onBack={() => setSection(null)} />}
      {section === "inventory" && <InventoryCompartment vault={vault} compartmentId={COMPARTMENT.INVENTORY} onBack={() => setSection(null)} />}
      {section === "emergency" && <EmergencyCardExport  vault={vault} onBack={() => setSection(null)} />}
      {section === "backup"    && <BackupPanel          vault={vault} onBack={() => setSection(null)} />}
      {!section && <VaultHome vault={vault} onOpen={setSection} onOpenBackup={() => setSection("backup")} />}
    </div>
  );
}

function VaultHome({ vault, onOpen, onOpenBackup }) {
  return (
    <div className="max-w-3xl mx-auto px-4" data-testid="private-vault-home">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-emerald-700">Private · This device only</div>
          <h1 className="font-serif font-900 text-3xl text-slate-900 flex items-center gap-2">
            <Shield className="w-6 h-6 text-emerald-600" /> Private Vault
          </h1>
        </div>
        <button onClick={() => vault.lock()} data-testid="vault-lock"
          className="px-3 py-2 border border-slate-300 hover:border-rose-400 text-sm font-700 rounded inline-flex items-center gap-1.5">
          <Lock className="w-4 h-4" /> Lock
        </button>
      </div>

      <BackupReminderBanner onOpenBackup={onOpenBackup} />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Tile testid="tile-health"    onClick={() => onOpen("health")}    title="Health & Emergency" subtitle="Vitals · meds · contacts · allergies" tint="from-rose-100 to-white border-rose-200 hover:border-rose-400"     iconColor="bg-rose-600"    Icon={HeartPulse} />
        <Tile testid="tile-inventory" onClick={() => onOpen("inventory")} title="Household Inventory" subtitle="Serial numbers, warranties, replacement values"       tint="from-amber-100 to-white border-amber-200 hover:border-amber-400" iconColor="bg-amber-600"   Icon={Boxes} />
        <Tile testid="tile-emergency" onClick={() => onOpen("emergency")} title="Emergency Card"     subtitle="Generate offline HTML/PDF to share or print"          tint="from-orange-100 to-white border-orange-200 hover:border-orange-400" iconColor="bg-orange-600"  Icon={LifeBuoy} />
        <Tile testid="tile-backup"    onClick={() => onOpen("backup")}    title="Backup & Restore"    subtitle="Encrypted file for cross-device transfer"             tint="from-indigo-100 to-white border-indigo-200 hover:border-indigo-400" iconColor="bg-indigo-600"  Icon={ShieldCheck} />
      </div>

      {!vault.biometricEnrolled && vault.biometricAvailable && (
        <div className="mt-4 p-3 bg-indigo-50 border border-indigo-200 rounded flex items-center gap-3" data-testid="enroll-bio-cta">
          <Fingerprint className="w-6 h-6 text-indigo-600 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-700 text-slate-900">Enable biometric unlock</div>
            <div className="text-xs text-slate-600">Use Face ID / Touch ID next time — PIN still works as fallback</div>
          </div>
          <button onClick={async () => { try { await vault.enrollBio(); } catch { /* toast handled inside */ } }}
            data-testid="enroll-bio-button"
            className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-700 rounded">Enable</button>
        </div>
      )}

      <div className="mt-6 p-3 bg-slate-100 rounded text-[11px] text-slate-600 flex items-start gap-2">
        <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
        <span>All data lives on this device only — AES-GCM 256 + PBKDF2 200k. Auto-locks after 5 minutes idle or when this tab is hidden for 60s+.</span>
      </div>
    </div>
  );
}

function Tile({ onClick, title, subtitle, tint, iconColor, Icon, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`group relative text-left bg-gradient-to-br border-2 rounded-lg p-4 transition-all hover:-translate-y-0.5 hover:shadow-md ${tint}`}>
      <div className="flex items-center gap-3">
        <div className={`w-11 h-11 rounded-full ${iconColor} grid place-items-center shadow-sm`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-serif font-800 text-lg text-slate-900 leading-tight">{title}</div>
          <div className="text-xs text-slate-600 mt-0.5">{subtitle}</div>
        </div>
        <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-slate-700" />
      </div>
    </button>
  );
}
